<template>
  <vue-slider
    v-model="dots"
    class="multiple-slider multiple-position-select"
    tooltip="none"
    :class="{ hoverable: rangeHoverable }"
    :max="preparedMax"
    :dot-options="dotOptions"
    :disabled="disabled"
    :use-keyboard="true"
    :keydown-hook="keyboardControl"
    v-bind="options"
  >
    <template v-slot:dot="dot">
      <span
        class="triangle-pointer"
        :class="{
          'start-pointer': dot.index % 2 === 0,
          'end-pointer': dot.index % 2 === 1
        }"
        :style="{
          color: getPointerColor(dot.index)
        }"
      >
        <div class="timeBlock" v-if="dot.index % 2 === 1">{{ timeRange[dot.index] }}</div>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 14 132'
          width='14'
          height='132'
          style="flex-shrink: 0"
        >
          <path
            d='M 0 0 L 14 0 L 7 7'
            fill='currentColor'
          />
          <path
            d='M 0 132 L 14 132 L 7 125'
            fill='currentColor'
          />
          <path
            d='M 7 0 L 7 132'
            stroke='currentColor'
            stroke-width='6'
          />
        </svg>
        <div class="timeBlock" v-if="dot.index % 2 === 0">{{ timeRange[dot.index] }}</div>
      </span>
    </template>

    <template v-slot:process="{ style, index }">
      <div
        class="vue-slider-process"
        :style="[style]"
        @mousedown="handleRangeClick(index, $event)"
      ></div>
    </template>
  </vue-slider>
</template>

<script>
import VueSlider from "vue-slider-component";
import {
  formatDurationWithMs,
  roundToMilliseconds
} from "@/utils/time.js";

// @group Sliders
// Slider for selecting multiple not-crossing ranges
export default {
  name: "MultipleRangesSlider",
  components: {
    VueSlider
  },
  model: {
    prop: "intervals",
    event: "update"
  },
  props: {
    /** ranges tuples */
    intervals: {
      type: Array,
      default: () => [[1, 2]],
      validator(val) {
        let last = -1;

        for (const [start, end] of val) {
          if (start < last || end < start) {
            return false;
          }
          last = end;
        }

        return true;
      }
    },
    /** Max time value */
    maxValue: {
      type: Number,
      required: true
    },
    /** CSS rules for ranges space */
    processStyle: {
      type: String,
      default: ""
    },
    /** is it possible to click range */
    rangeHoverable: {
      type: Boolean,
      default: false
    },
    /** is slider disabled */
    disabled: {
      type: Boolean,
      default: false
    }
  },
  sliderColor: [
    "#13d413",
    "#9715c2",
    "#c21540",
    "#1549c2",
    "#bfc215",
    "#de9331",
    "#31dead",
    "#de318d",
    "#ffffff",
    "#a6be22",
    "#67c91c",
    "#1c9dc9",
    "#291cc9",
    "#ae1cc9",
    "#c91c1c",
    "#b7c91c",
    "#c9671c",
    "#c9c91c",
    "#05c288",
    "#057ec2",
    "#ac4848",
    "#c91c1c",
    "#b7c91c",
    "#c9671c",
    "#c9c91c",
    "#05c288",
    "#057ec2",
    "#a6be22",
    "#67c91c",
    "#1c9dc9",
    "#291cc9",
    "#ae1cc9",
    "#c91c1c",
    "#b7c91c"
  ],
  selectColor: "#dead31",
  data() {
    return {
      options: {
        width: "100%",
        min: 0,
        interval: 0.001,
        duration: 0,
        clickable: false,
        tooltipFormatter: this.getTime,
        process: this.getProcessStyles,
        processStyle: this.processStyle,
        dotStyle: {
          width: "3px",
          "border-radius": "0",
          "box-shadow": "none"
        }
      }
    };
  },
  computed: {
    preparedMax() {
      return roundToMilliseconds(this.maxValue);
    },
    timeRange() {
      return this.dots.map(value => formatDurationWithMs(value));
      // const timestamp = this.intervals[0][0];
      // const hours = Math.floor(timestamp / 60 / 60);
      // const minutes = Math.floor(timestamp / 60) - hours * 60;
      // const seconds = timestamp % 60;
      // // var formatted = hours + ":" + minutes + ":" + seconds;
      // const formatted = [
      //   hours.toString().padStart(2, "0"),
      //   minutes.toString().padStart(2, "0"),
      //   seconds.toString().padStart(2, "0")
      // ].join(":");
      // return formatted;
    },
    intervalDots() {
      const dots = [];
      for (const [start, end] of this.intervals) {
        dots.push(roundToMilliseconds(start));
        dots.push(roundToMilliseconds(end));
      }
      return dots;
    },
    dots: {
      get() {
        return this.intervalDots;
      },
      set(dots) {
        const intervals = [];
        /** we use dots.length - 1 to prevent 'select dot' transmitting to update */
        const sanitizedDots = dots.map(value => roundToMilliseconds(value));
        for (let i = 0; i < sanitizedDots.length - 1; i += 2) {
          intervals.push([sanitizedDots[i], sanitizedDots[i + 1]]);
        }
        this.$emit("update", intervals);
      }
    },
    dotOptions() {
      return [];
    }
  },
  methods: {
    getTime(seconds) {
      return formatDurationWithMs(seconds);
    },
    getPointerColor(index) {
      if (index === this.intervalDots.length) {
        return this.$options.sliderColor;
      }

      return this.$options.sliderColor[Math.floor(index / 2)];
    },
    getProcessStyles(dots) {
      const intervals = [];
      for (let i = 0; i < dots.length; i += 2) {
        const style = {
          "background-color": this.$options.sliderColor[i / 2],
          margin: `-120px 0`,
          height: `124px`,
          "border-radius": `0px`,
          opacity: "0.5"
        };
        intervals.push([dots[i], dots[i + 1], style]);
      }
      return intervals;
    },
    handleRangeClick(index, event) {
      this.$emit("rangeClicked", { index, event });
    },
    keyboardControl(e) {
      const d = e.shiftKey ? 1000 : 1;
      const direction = 'ltr';
      switch (e.keyCode) {
        case 38: // up
          return i => (direction === 'ttb' ? i - 1 : i + 1)
        case 39: // right
          return i => (direction === 'rtl' ? i - d : i + d)
        case 40: // down
          return i => (direction === 'ttb' ? i + 1 : i - 1)
        case 37: // left
          return i => (direction === 'rtl' ? i + d : i - d)

        case 35: // end
          return () => this.preparedMax
        case 36: // home
          return () => 0

        case 33: // page down
          return i => i + 10
        case 34: // page up
          return i => i - 10

        default:
          return null
      }
    }
  }
};
</script>

<style scoped lang="scss">
.timeBlock {
  color: #ffffff;
  // font-family: sans-serif;
  font-size: 13px;
  margin: 8px -4px;
  pointer-events: none;
  align-self: flex-start;
  background: #000;
  padding: 2px 4px;
}

.start-pointer > .timeBlock {
  align-self: flex-end;
}

.vue-slider-dot-focus .timeBlock {
  font-weight: bold;
}

.triangle-pointer {
  cursor: ew-resize;
  position: absolute;
  left: 0;
  font-size: 20px;
  white-space: nowrap;
  display: flex;

  margin-top: -122px;
  height: 132px;
  width: 14px;

  &.start-pointer {
    justify-content: flex-start;
  }

  &.end-pointer {
    justify-content: flex-end;
  }

}

.multiple-slider {
  width: 100%;
}
</style>

<style lang="scss">
.multiple-position-select .vue-slider-dot.vue-slider-dot-hover {
  padding: 0px;
  width: 10px !important;
  cursor: pointer;
  /** transform: translate(-55%, -20%) !important; */
}

.vue-slider-ltr {
  padding: 0px !important;
}

.multiple-slider {
  .vue-slider-dot-handle {
    border-radius: 0;
  }

  &.hoverable {
    .vue-slider-process:hover {
      border: 1px solid yellow;
    }
  }
}
</style>
